import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { rem } from 'polished';
import Box from '../../components/Box/Box';
import ChevronRight from '../../images/icons/chevron-right.svg';
import {
    Body1,
    Body2,
    Caption,
    Head6,
    Subheading1,
} from '../../components/Typography/Typography';
import { theme } from '../../../config/theme';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import { CardListSidebar } from '../../components/CardListSidebar';
import ArrowLeft from '../../images/icons/arrow-left.svg';
import { graphql } from 'gatsby';
import WithSection from '../../components/WithSection/WithSection';
import RichText from '../../components/RichText/RichText';
import { CardListItems } from '../../components/CardListItems/CardListItems';
import Grid from '../../components/Grid/Grid';

const IntegrationsSectionContent = styled(Box)``;

const IntegrationsCard = ({
    data,
    setCurrentClickedItem,
    hasTemplateDetails,
}) => {
    const {
        logo,
        name,
        description,
        cta_text,
        cta_tracking_event,
        hint,
        by_name,
        by_icon,
    } = data;

    return (
        <Box
            borderRadius={theme.radii.sm}
            border={`1px solid ${theme.colors.primary[100]}`}
            padding={rem(16)}
            css={css`
                display: flex;
                flex-direction: column;
            `}
            transition="all 200ms ease"
        >
            <Box
                display="flex"
                flexDirection={hasTemplateDetails ? 'column' : 'row'}
                alignItems={hasTemplateDetails ? 'flex-start' : 'center'}
                css={css`
                    gap: ${rem(8)};
                `}
                marginBottom={rem(16)}
            >
                {Boolean(logo?.url) && (
                    <Box
                        as={Image}
                        img={logo}
                        width={rem(50)}
                        height={rem(50)}
                    />
                )}
                <Subheading1 fontWeight={600}>{name}</Subheading1>
            </Box>
            <Body1 marginBottom={rem(28)} color={theme.colors.primary[1600]}>
                {description}
            </Body1>
            {Boolean(hint) && (
                <Body2
                    marginTop="auto"
                    color={theme.colors.secondary[1300]}
                    bold
                    css={css`
                        &:not(:last-child) {
                            margin-bottom: ${rem(28)};
                        }
                    `}
                >
                    {hint}
                </Body2>
            )}

            {!by_icon.url && !by_name && !cta_text ? null : (
                <Box
                    display={'flex'}
                    marginTop="auto"
                    justifyContent="space-between"
                >
                    {(Boolean(by_icon.url) || Boolean(by_name)) && (
                        <Box
                            css={css`
                                display: flex;
                                align-items: center;
                                gap: ${rem(8)};
                            `}
                        >
                            {Boolean(by_icon.url) && (
                                <Box
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                    `}
                                    size={rem(32)}
                                    backgroundColor="primary.50"
                                    borderRadius={theme.radii.sm}
                                >
                                    <Box
                                        as={Image}
                                        size={rem(24)}
                                        img={by_icon}
                                        alt={by_icon.alt}
                                    />
                                </Box>
                            )}
                            {Boolean(by_name) && <Caption>{by_name}</Caption>}
                        </Box>
                    )}
                    {cta_text && (
                        <PrismicLink link={data.cta_link}>
                            <Button
                                size="md"
                                variant="tertiary"
                                endIcon={
                                    <Box as={ChevronRight} size={rem(16)} />
                                }
                                onClick={() => setCurrentClickedItem?.(data)}
                                trackingEvent={cta_tracking_event}
                            >
                                {cta_text}
                            </Button>
                        </PrismicLink>
                    )}
                </Box>
            )}
        </Box>
    );
};

const TemplatePreviewItem = ({ currentClickedItem, setCurrentClickedItem }) => {
    const { cta_1_text, cta_2_text, description, details, title } =
        currentClickedItem?.templateDetails;

    if (currentClickedItem === null) return null;
    return (
        <Box>
            <Button
                mb={rem(34)}
                startIcon={<Box as={ArrowLeft} />}
                variant="tertiary"
                onClick={() => {
                    setCurrentClickedItem?.(null);
                }}
            >
                Back
            </Button>
            <Box mb={rem(24)} as={Head6}>
                {title?.text}
            </Box>
            <Box as={Body1} mb={rem(24)} color="primary.1600">
                {description}
            </Box>
            <Button
                as={PrismicLink}
                link={{ url: currentClickedItem?.redirection_url }}
                marginBottom={rem(24)}
                size="lg"
            >
                {cta_1_text}
            </Button>
            <RichText render={details?.richText} />
            <Button
                as={PrismicLink}
                link={{ url: currentClickedItem?.redirection_url }}
                marginTop={rem(24)}
                size="lg"
            >
                {cta_2_text}
            </Button>
        </Box>
    );
};

const IntegrationsSection = ({ data }) => {
    const templateDetailsObject =
        data.primary.templates_details.document?.data?.body?.[0];
    const hasTemplateDetails = data.primary.templates_details.document;

    const cardItems = data.items.reduce((acc, curr) => {
        const currentCategory = curr.cards_category.document.data;

        const cards = currentCategory.cards.map((card) => {
            const cardId = card.card.document.id;
            const templateDetails = templateDetailsObject?.items?.filter?.(
                (item) => item.id === cardId
            );
            return {
                ...card.card.document.data,
                id: cardId,
                category: currentCategory.category,
                ...(hasTemplateDetails && {
                    templateDetails,
                    redirection_url: `${templateDetailsObject?.primary?.redirection_url}${cardId}`,
                }),
            };
        });
        return [...acc, ...cards];
    }, []);

    const categoryNameList = data.items.reduce((acc, curr) => {
        const currentCategory = curr.cards_category.document.data;
        return [...acc, currentCategory.category];
    }, []);

    const { sidebar_option_1, sidebar_title_1, sidebar_title_2 } = data.primary;
    const [items, setItems] = useState(cardItems);
    const [activeTab, setActiveTab] = useState(sidebar_option_1);
    const [currentClickedItem, setCurrentClickedItem] = useState(null);

    const filterByCategory = (categoryName) => {
        setCurrentClickedItem(null);
        const filteredItems = cardItems.filter(
            (integration) => integration.category === categoryName
        );

        setItems(categoryName === sidebar_option_1 ? cardItems : filteredItems);
    };

    return (
        <Grid gridColumn="1/-1">
            <CardListSidebar
                sidebarTitle1={sidebar_title_1}
                sidebarTitle2={sidebar_title_2}
                sidebarOption1={sidebar_option_1}
                categoryNameList={categoryNameList}
                allItems={cardItems}
                setItems={setItems}
                filterByCategory={filterByCategory}
                setCurrentClickedItem={setCurrentClickedItem}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                id="blogListSection"
                scrollMarginTop="var(--header-height)"
                listContainerId="full-integrations"
            />
            <IntegrationsSectionContent
                marginTop={{ _: rem(8), sm: 0 }}
                gridColumn={{ _: '1/-1', sm: 'span 6', lg: 'span 8' }}
            >
                {hasTemplateDetails && currentClickedItem ? (
                    <TemplatePreviewItem
                        currentClickedItem={currentClickedItem}
                        setCurrentClickedItem={setCurrentClickedItem}
                    />
                ) : (
                    <CardListItems
                        items={items}
                        activeTab={activeTab}
                        renderItem={(item) => (
                            <IntegrationsCard
                                data={item}
                                setCurrentClickedItem={setCurrentClickedItem}
                                hasTemplateDetails={hasTemplateDetails}
                            />
                        )}
                        gridTemplateColumns={{
                            _: '1fr',
                            xs: 'repeat(2, 1fr)',
                            xl: `repeat(3, 1fr)`,
                        }}
                    />
                )}
            </IntegrationsSectionContent>
        </Grid>
    );
};

export const _ = graphql`
    fragment FILTER_CARDS on PrismicSectionDataBodyFilterCards {
        primary {
            sidebar_option_1
            sidebar_title_1
            sidebar_title_2
            templates_details {
                document {
                    ... on PrismicTemplatesDetails {
                        data {
                            body {
                                ... on PrismicTemplatesDetailsDataBodyTemplates {
                                    items {
                                        cta_1_text
                                        cta_2_text
                                        description
                                        details {
                                            richText
                                        }
                                        title {
                                            text
                                        }
                                        id
                                    }
                                    primary {
                                        redirection_url
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        items {
            cards_category {
                document {
                    ... on PrismicCardsCategory {
                        id
                        data {
                            category
                            cards {
                                card {
                                    document {
                                        ... on PrismicCardInfo {
                                            id
                                            data {
                                                name
                                                description
                                                cta_text
                                                cta_tracking_event
                                                by_name
                                                by_icon {
                                                    url
                                                    alt
                                                }
                                                hint
                                                logo {
                                                    url
                                                    alt
                                                }
                                                id
                                                cta_link {
                                                    ...PRISMIC_LINK_FRAGMENT
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;

export default WithSection(IntegrationsSection);
